import { useMemo, useState, type ReactNode } from "react"
import { Box, Flex, Heading, Text } from "@chakra-ui/react"
import { Chart, useChart } from "@chakra-ui/charts"
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis, ResponsiveContainer, Line, ReferenceArea, Legend } from "recharts"
import type { Graph, Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"
import type { DateTime } from "timezonecomplete"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import {
  appendTimeMs,
  chartHighlightToMsRange,
  TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX,
  timescaleChartMarginRight,
  timescaleMinTickGap,
  timescaleSpanMs,
  timescaleTickFormatter,
  timescaleTimeDomain,
  type TimeSeriesDataPoint,
  type TimeSeriesSeries,
  type ChartHighlightRange,
} from "./TimeSeries"
import { BATTERY_COLORS, OVERLAY_LINE_COLOR } from "./graphColors"

const TIMESCALE_MONITORING_CHART_HEIGHT = "400px"

function buildBatteryPlaceholderChart(
  startDate: DateTime,
  endDate: DateTime
): TimeSeriesDataPoint[] {
  const start = startDate.toIsoString()
  const end = endDate.toIsoString()
  return [
    { timestamp: start, discharge: 0, charge: 0, soc: 50 },
    { timestamp: end, discharge: 0, charge: 0, soc: 50 },
  ]
}

interface BatteryChartProps {
  dischargeData: TimeSeriesDataPoint[]
  dischargeSeries: TimeSeriesSeries<string[]>[]
  chargeData: TimeSeriesDataPoint[]
  chargeSeries: TimeSeriesSeries<string[]>[]
  socData: TimeSeriesDataPoint[]
  socSeries: TimeSeriesSeries<string[]>[]
  graph: Graph
  /** Used to render a grey sample chart when there is no real data */
  chartAxisStartDate: DateTime
  chartAxisEndDate: DateTime
  timeseries?: Timeseries
  /** Time ranges to highlight on the chart (e.g. alert event periods) */
  highlightRanges?: ChartHighlightRange[]
  /** Shared timestamps across all graphs for x-axis alignment */
  sharedTimestamps?: string[]
  /** When set, matches other Timescale monitoring charts' X tick spacing */
  xAxisTickInterval?: number
  /** Shared tick positions (ms) so battery aligns with power/load charts */
  xAxisTicksMs?: number[]
  /** Control shown on the title row, filling the space to the right of the title (e.g. an asset dropdown). */
  headerEndContent?: ReactNode
}

const formatValueWithDecimals = (value: number): string => {
  if (Math.abs(value) < 1) {
    return value.toFixed(4)
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

interface BatteryTooltipProps {
  active?: boolean
  payload?: any[]
  label?: string
  unit?: string
}

interface BatteryLegendProps {
  payload?: readonly any[]
  onSeriesHover: (seriesName: string | null) => void
  hoveredSeries: string | null
}

const BatteryLegend = ({ payload, onSeriesHover, hoveredSeries }: BatteryLegendProps) => {
  if (!payload || !payload.length) return null

  const handleMouseEnter = (seriesKey: string) => {
    setTimeout(() => onSeriesHover(seriesKey), 0)
  }

  const handleMouseLeave = () => {
    setTimeout(() => onSeriesHover(null), 0)
  }

  return (
    <Flex direction="row" gap={4} justifyContent="center" alignItems="center" mb={2} flexWrap="wrap">
      {payload.map((entry, index) => {
        const seriesKey = entry.value || entry.dataKey
        const displayLabel = entry.name || seriesKey
        const isLine = seriesKey === "soc"

        return (
          <Flex
            key={`legend-${index}`}
            data-legend-item
            alignItems="center"
            gap={1}
            cursor="pointer"
            opacity={hoveredSeries && hoveredSeries !== seriesKey ? 0.5 : 1}
            transition="opacity 0.15s ease-in-out"
            onMouseOver={() => handleMouseEnter(seriesKey)}
            onMouseLeave={handleMouseLeave}
            fontSize="sm"
          >
            <Box
              width="12px"
              height={isLine ? "2px" : "12px"}
              backgroundColor={entry.color}
              borderRadius={isLine ? "0" : "sm"}
            />
            <Text color="primary">{displayLabel}</Text>
          </Flex>
        )
      })}
    </Flex>
  )
}

const BatteryTooltip = ({ active, payload, label, unit }: BatteryTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const labelMs = typeof label === "number" ? label : new Date(String(label || "")).getTime()
  const date = new Date(Number.isFinite(labelMs) ? labelMs : String(label || ""))
  const formattedDate = date.toLocaleString([], {
    timeZone: IANATimeZone(),
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const chargeValue = payload.find((p) => p.dataKey === "charge")?.value
  const dischargeValue = payload.find((p) => p.dataKey === "discharge")?.value
  const socValue = payload.find((p) => p.dataKey === "soc")?.value

  return (
    <Box
      backgroundColor="card"
      borderWidth="1px"
      borderColor="border"
      borderRadius="md"
      padding={3}
      boxShadow="lg"
      minW="250px"
    >
      <Text fontSize="sm" fontWeight="bold" color="primary" mb={2}>
        {formattedDate}
      </Text>

      <Flex direction="column" gap={2}>
        {typeof chargeValue === "number" && Number.isFinite(chargeValue) && (
          <Flex alignItems="center" gap={2}>
            <Box w="12px" h="12px" backgroundColor={BATTERY_COLORS.charge[0]} borderRadius="sm" />
            <Text fontSize="sm" color="primary" flex="1">
              Charge
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="primary">
              {formatValueWithDecimals(chargeValue)}{unit ? ` ${unit}` : ""}
            </Text>
          </Flex>
        )}
        {typeof dischargeValue === "number" && Number.isFinite(dischargeValue) && (
          <Flex alignItems="center" gap={2}>
            <Box w="12px" h="12px" backgroundColor={BATTERY_COLORS.discharge[0]} borderRadius="sm" />
            <Text fontSize="sm" color="primary" flex="1">
              Discharge
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="primary">
              {formatValueWithDecimals(dischargeValue)}{unit ? ` ${unit}` : ""}
            </Text>
          </Flex>
        )}
        {typeof socValue === "number" && Number.isFinite(socValue) && (
          <Flex alignItems="center" gap={2} mt={1} pt={2} borderTopWidth="1px" borderColor="border">
            <Box w="12px" h="2px" backgroundColor={BATTERY_COLORS.soc} />
            <Text fontSize="sm" color="primary" flex="1">
              SoC
            </Text>
            <Text fontSize="sm" fontWeight="semibold" color="primary">
              {formatValueWithDecimals(socValue)} %
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

const BatteryChart = ({
  dischargeData,
  dischargeSeries,
  chargeData,
  chargeSeries,
  socData,
  socSeries,
  graph,
  chartAxisStartDate,
  chartAxisEndDate,
  timeseries,
  highlightRanges,
  sharedTimestamps,
  xAxisTickInterval,
  xAxisTicksMs,
  headerEndContent,
}: BatteryChartProps) => {
  const mergedData = useMemo(() => {
    const timestampSet = new Set<string>()
    dischargeData.forEach((p) => timestampSet.add(p.timestamp as string))
    chargeData.forEach((p) => timestampSet.add(p.timestamp as string))
    socData.forEach((p) => timestampSet.add(p.timestamp as string))

    // Use shared timestamps if available, otherwise use local timestamps
    const timestamps = sharedTimestamps || Array.from(timestampSet).sort()

    const dischargeMap = new Map(
      dischargeData.map((p) => {
        const total = dischargeSeries.reduce((sum, s) => {
          const val = p[s.name]
          return sum + (typeof val === "number" && Number.isFinite(val) ? val : 0)
        }, 0)
        return [p.timestamp, total]
      })
    )

    const chargeMap = new Map(
      chargeData.map((p) => {
        const total = chargeSeries.reduce((sum, s) => {
          const val = p[s.name]
          return sum + (typeof val === "number" && Number.isFinite(val) ? val : 0)
        }, 0)
        return [p.timestamp, total]
      })
    )

    const socMap = new Map(
      socData.map((p) => {
        const avgSoc = socSeries.length > 0
          ? socSeries.reduce((sum, s) => {
              const val = p[s.name]
              return sum + (typeof val === "number" && Number.isFinite(val) ? val : 0)
            }, 0) / socSeries.length
          : null
        return [p.timestamp, avgSoc]
      })
    )

    return timestamps.map((timestamp) => ({
      timestamp,
      discharge: dischargeMap.get(timestamp) ?? null,
      charge: chargeMap.get(timestamp) ?? null,
      soc: socMap.get(timestamp) ?? null,
    }))
  }, [dischargeData, dischargeSeries, chargeData, chargeSeries, socData, socSeries, sharedTimestamps])

  const hasRealBatteryData = useMemo(() => {
    if (mergedData.length === 0) return false
    return mergedData.some(
      (row) =>
        (typeof row.discharge === "number" && Number.isFinite(row.discharge) && row.discharge !== 0) ||
        (typeof row.charge === "number" && Number.isFinite(row.charge) && row.charge !== 0) ||
        (typeof row.soc === "number" && Number.isFinite(row.soc))
    )
  }, [mergedData])

  const chartData = useMemo(() => {
    if (!hasRealBatteryData) {
      return buildBatteryPlaceholderChart(chartAxisStartDate, chartAxisEndDate)
    }
    return mergedData
  }, [mergedData, hasRealBatteryData, chartAxisStartDate, chartAxisEndDate])

  const isPlaceholder = !hasRealBatteryData

  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null)

  const isMobile = useMemo(() => {
    return window.innerWidth < 768
  }, [])

  const chartSeries = useMemo(
    () => [
      { name: "discharge", color: BATTERY_COLORS.discharge[0], label: "Discharge", strokeWidth: 0, connectNulls: false },
      { name: "charge", color: BATTERY_COLORS.charge[0], label: "Charge", strokeWidth: 0, connectNulls: false },
      { name: "soc", color: OVERLAY_LINE_COLOR, label: "SoC", strokeWidth: 1, connectNulls: true },
    ],
    []
  )

  const chartDataWithTime = useMemo(() => appendTimeMs(chartData), [chartData])

  const batteryTimeDomain = useMemo(
    () => timescaleTimeDomain(xAxisTicksMs, chartAxisStartDate, chartAxisEndDate) as [number, number],
    [chartAxisStartDate, chartAxisEndDate, xAxisTicksMs]
  )
  const batteryTimeMinTickGap = useMemo(
    () => timescaleMinTickGap(xAxisTickInterval, chartDataWithTime),
    [xAxisTickInterval, chartDataWithTime]
  )
  const formatBatteryTimeTick = useMemo(
    () => timescaleTickFormatter(timescaleSpanMs(chartAxisStartDate, chartAxisEndDate)),
    [chartAxisStartDate, chartAxisEndDate]
  )
  const chart = useChart({
    data: chartDataWithTime,
    series: chartSeries,
  } as unknown as Parameters<typeof useChart>[0])

  const formatYAxisTick = (value: number) => {
    if (Math.abs(value) < 1) {
      return value.toFixed(4)
    }
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  const unit = useMemo(() => {
    if (graph.source?.desiredUnit) {
      return graph.source.desiredUnit
    }
    const yAxisMatch = graph.yAxis?.match(/\(([^)]+)\)/)
    return yAxisMatch?.[1] || undefined
  }, [graph])

  return (
    <Box position="relative" opacity={isPlaceholder ? 0.55 : 1}>
      <Flex mb={4} alignItems="center" justifyContent="space-between" gap={3} flexWrap="wrap">
        <Flex alignItems="center" minW={0}>
          <Heading fontFamily="montserrat" size="sm">
            {graph.title}
          </Heading>
        </Flex>
        {headerEndContent ? (
          <Flex flex="1" justifyContent="flex-end" minW={{ base: "100%", md: "240px" }}>
            {headerEndContent}
          </Flex>
        ) : null}
      </Flex>
      <Chart.Root h={TIMESCALE_MONITORING_CHART_HEIGHT} maxH={TIMESCALE_MONITORING_CHART_HEIGHT} chart={chart}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartDataWithTime}
            barCategoryGap="6%"
            margin={{
              top: 5,
              right: timescaleChartMarginRight(true),
              left: 20,
              bottom: 60,
            }}
          >
            <CartesianGrid stroke={chart.color("border")} strokeDasharray="3 3" vertical={false} />
            {highlightRanges?.map((range, index) => {
              const { x1, x2 } = chartHighlightToMsRange(range)
              return (
              <ReferenceArea
                key={`highlight-${index}`}
                yAxisId="left"
                x1={x1}
                x2={x2}
                fill={range.color || "rgba(255, 165, 0, 0.2)"}
                fillOpacity={0.3}
                stroke={range.color || "rgba(255, 165, 0, 0.6)"}
                strokeWidth={4}
                label={
                  range.label
                    ? {
                        value: range.label,
                        position: "insideTop",
                        fill: "#666",
                        fontSize: 11,
                      }
                    : undefined
                }
              />
            )
            })}
            <XAxis
              axisLine={false}
              dataKey="timeMs"
              type="number"
              scale="time"
              domain={batteryTimeDomain}
              stroke={chart.color("chart_axis")}
              fontSize={12}
              tickLine={false}
              tickFormatter={formatBatteryTimeTick}
              ticks={xAxisTicksMs}
              minTickGap={xAxisTicksMs?.length ? undefined : batteryTimeMinTickGap}
            />
            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              stroke={chart.color("chart_axis")}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              label={
                graph?.yAxis
                  ? {
                      value: graph.yAxis,
                      angle: -90,
                      position: "insideLeft",
                    }
                  : undefined
              }
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              width={TIMESCALE_SECONDARY_RIGHT_YAXIS_WIDTH_PX}
              axisLine={false}
              tickLine={false}
              tickMargin={10}
              stroke={chart.color("chart_axis")}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              label={{
                value: "SOC (%)",
                angle: 90,
                position: "insideRight",
              }}
            />
            <Tooltip animationDuration={100} content={<BatteryTooltip unit={unit} />} />
            {!isMobile && (
              <Legend
                verticalAlign="top"
                align="center"
                content={(props) => (
                  <BatteryLegend
                    {...props}
                    onSeriesHover={setHoveredSeries}
                    hoveredSeries={hoveredSeries}
                  />
                )}
              />
            )}
            <Bar
              yAxisId="left"
              isAnimationActive={false}
              dataKey="discharge"
              fill={BATTERY_COLORS.discharge[0]}
              fillOpacity={hoveredSeries && hoveredSeries !== "discharge" ? 0.5 : 1}
              maxBarSize={80}
              name="Discharge"
            />
            <Bar
              yAxisId="left"
              isAnimationActive={false}
              dataKey="charge"
              fill={BATTERY_COLORS.charge[0]}
              fillOpacity={hoveredSeries && hoveredSeries !== "charge" ? 0.5 : 1}
              maxBarSize={80}
              name="Charge"
            />
            <Line
              yAxisId="right"
              isAnimationActive={false}
              dataKey="soc"
              stroke={OVERLAY_LINE_COLOR}
              strokeWidth={1}
              strokeOpacity={hoveredSeries && hoveredSeries !== "soc" ? 0.5 : 1}
              dot={false}
              connectNulls={true}
              type="monotone"
              name="SoC"
            />
          </BarChart>
        </ResponsiveContainer>
      </Chart.Root>
    </Box>
  )
}

export default BatteryChart
